import { html, nothing, type TemplateResult } from "lit";
import { t } from "../i18n.ts";

type SetupStep = {
  text: string;
};

// Per-channel config fields for the quick-start form.
type ChannelField = {
  key: string;
  label: string;
  placeholder?: string;
  sensitive?: boolean;
  type?: "text" | "number" | "toggle";
  help?: string;
};

const CHANNEL_FIELDS: Record<string, ChannelField[]> = {
  telegram: [
    {
      key: "botToken",
      label: "Bot Token",
      placeholder: "123456789:ABCdef...",
      sensitive: true,
      help: "Get this from @BotFather on Telegram",
    },
  ],
  discord: [
    {
      key: "token",
      label: "Bot Token",
      placeholder: "MTIzNDU2Nzg5.Gh...",
      sensitive: true,
      help: "From Discord Developer Portal > Bot > Token",
    },
  ],
  slack: [
    {
      key: "botToken",
      label: "Bot Token",
      placeholder: "xoxb-...",
      sensitive: true,
      help: "OAuth Bot User token",
    },
    {
      key: "appToken",
      label: "App Token",
      placeholder: "xapp-...",
      sensitive: true,
      help: "Socket Mode app-level token",
    },
  ],
  signal: [
    {
      key: "account",
      label: "Phone Number",
      placeholder: "+1234567890",
      help: "The phone number linked to signal-cli",
    },
    {
      key: "httpUrl",
      label: "HTTP URL",
      placeholder: "http://localhost:8080",
      help: "signal-cli REST API endpoint",
    },
  ],
  line: [
    {
      key: "channelAccessToken",
      label: "Channel Access Token",
      placeholder: "your-token...",
      sensitive: true,
    },
    {
      key: "channelSecret",
      label: "Channel Secret",
      placeholder: "your-secret...",
      sensitive: true,
    },
  ],
  irc: [
    { key: "host", label: "Server", placeholder: "irc.libera.chat" },
    { key: "port", label: "Port", placeholder: "6697", type: "number" },
    { key: "nick", label: "Nickname", placeholder: "my-bot" },
    { key: "tls", label: "Use TLS", type: "toggle" },
  ],
  msteams: [
    { key: "appId", label: "App ID", placeholder: "your-app-id" },
    {
      key: "appPassword",
      label: "App Password",
      placeholder: "your-app-password",
      sensitive: true,
    },
  ],
  matrix: [
    {
      key: "homeserverUrl",
      label: "Homeserver URL",
      placeholder: "https://matrix.example.com",
    },
    { key: "userId", label: "User ID", placeholder: "@bot:example.com" },
    {
      key: "accessToken",
      label: "Access Token",
      placeholder: "syt_...",
      sensitive: true,
    },
  ],
  nostr: [
    {
      key: "privateKey",
      label: "Private Key (nsec)",
      placeholder: "nsec1...",
      sensitive: true,
    },
  ],
  imessage: [
    {
      key: "cliPath",
      label: "CLI Path",
      placeholder: "/usr/local/bin/imsg",
    },
  ],
  googlechat: [
    {
      key: "serviceAccountKeyFile",
      label: "Service Account Key Path",
      placeholder: "/path/to/service-account.json",
    },
    {
      key: "projectNumber",
      label: "Project Number",
      placeholder: "123456789",
    },
  ],
};

type ChannelGuide = {
  steps: SetupStep[];
  docsUrl?: string;
  /** Example JSON config to show as a copyable template. */
  example?: string;
};

const CHANNEL_GUIDES: Record<string, ChannelGuide> = {
  telegram: {
    steps: [
      { text: "Open Telegram and search for @BotFather" },
      { text: "Type /newbot and follow the prompts to name your bot" },
      { text: "Copy the Bot Token and paste it below" },
      { text: 'Click "Save & Apply" to start' },
    ],
    docsUrl: "https://docs.nabe.ai/channels/telegram",
    example: `{
  "botToken": "123456789:ABCdef..."
}`,
  },
  whatsapp: {
    steps: [
      { text: "Just add WhatsApp and Save & Apply" },
      { text: "A QR code will appear on the Channels status page" },
      { text: "Open WhatsApp on your phone > Linked Devices > Link a Device" },
      { text: "Scan the QR code with your phone" },
    ],
    docsUrl: "https://docs.nabe.ai/channels/whatsapp",
    example: `{}`,
  },
  discord: {
    steps: [
      { text: "Go to Discord Developer Portal (discord.com/developers)" },
      { text: "Create a new Application, then add a Bot" },
      { text: "Enable Message Content Intent under Bot settings" },
      { text: "Copy the Bot Token and paste it below" },
      { text: "Invite the bot to your server using OAuth2 URL Generator" },
    ],
    docsUrl: "https://docs.nabe.ai/channels/discord",
    example: `{
  "token": "MTIzNDU2Nzg5.Gh..."
}`,
  },
  line: {
    steps: [
      { text: "Go to LINE Developers Console (developers.line.biz)" },
      { text: "Create a new Messaging API channel" },
      { text: "Copy the Channel Access Token and Channel Secret" },
      { text: "Paste them below" },
    ],
    docsUrl: "https://docs.nabe.ai/channels/line",
    example: `{
  "channelAccessToken": "your-token...",
  "channelSecret": "your-secret..."
}`,
  },
  slack: {
    steps: [
      { text: "Create a Slack App at api.slack.com/apps" },
      { text: "Enable Socket Mode and generate an App-Level Token (xapp-)" },
      { text: "Install the app to your workspace and copy the Bot Token (xoxb-)" },
      { text: "Paste both tokens below" },
    ],
    docsUrl: "https://docs.nabe.ai/channels/slack",
    example: `{
  "botToken": "xoxb-...",
  "appToken": "xapp-..."
}`,
  },
  signal: {
    steps: [
      { text: "Install signal-cli and link a phone number" },
      { text: "Start the signal-cli REST API (HTTP daemon)" },
      { text: "Enter the phone number and HTTP URL below" },
    ],
    docsUrl: "https://docs.nabe.ai/channels/signal",
    example: `{
  "account": "+1234567890",
  "httpUrl": "http://localhost:8080"
}`,
  },
  googlechat: {
    steps: [
      { text: "Create a Google Cloud project with Chat API enabled" },
      { text: "Create a service account and download the JSON key" },
      { text: "Configure the Chat app in Google Admin Console" },
      { text: "Set the service account key path or inline JSON below" },
    ],
    docsUrl: "https://docs.nabe.ai/channels/googlechat",
    example: `{
  "serviceAccountKeyFile": "/path/to/service-account.json",
  "projectNumber": "123456789"
}`,
  },
  imessage: {
    steps: [
      { text: "Requires macOS with Messages app configured" },
      { text: "Install the imsg CLI tool" },
      { text: "Configure the CLI path below" },
    ],
    docsUrl: "https://docs.nabe.ai/channels/imessage",
    example: `{
  "cliPath": "/usr/local/bin/imsg"
}`,
  },
  msteams: {
    steps: [
      { text: "Register an app in Azure Portal" },
      { text: "Create a Bot Channel Registration" },
      { text: "Copy the App ID and App Password" },
      { text: "Paste them below" },
    ],
    docsUrl: "https://docs.nabe.ai/channels/msteams",
    example: `{
  "appId": "your-app-id",
  "appPassword": "your-app-password"
}`,
  },
  matrix: {
    steps: [
      { text: "Create a bot user on your Matrix homeserver" },
      { text: "Generate an access token for the bot user" },
      { text: "Enter homeserver URL, user ID, and access token below" },
    ],
    docsUrl: "https://docs.nabe.ai/channels/matrix",
    example: `{
  "homeserverUrl": "https://matrix.example.com",
  "userId": "@bot:example.com",
  "accessToken": "syt_..."
}`,
  },
  nostr: {
    steps: [
      { text: "Generate or import a Nostr keypair (nsec)" },
      { text: "Configure your relay URLs" },
    ],
    docsUrl: "https://docs.nabe.ai/channels/nostr",
    example: `{
  "privateKey": "nsec1...",
  "relays": ["wss://relay.damus.io"]
}`,
  },
  irc: {
    steps: [
      { text: "Enter the IRC server hostname and port" },
      { text: "Choose a nickname for the bot" },
      { text: "Optionally configure NickServ password and channels" },
    ],
    docsUrl: "https://docs.nabe.ai/channels/irc",
    example: `{
  "host": "irc.libera.chat",
  "port": 6697,
  "nick": "my-bot",
  "tls": true
}`,
  },
};

function copyToClipboard(text: string, button: HTMLButtonElement) {
  void navigator.clipboard.writeText(text).then(() => {
    const original = button.textContent;
    button.textContent = "Copied!";
    setTimeout(() => {
      button.textContent = original;
    }, 1500);
  });
}

export function renderChannelSetupGuide(channelId: string): TemplateResult | typeof nothing {
  const guide = CHANNEL_GUIDES[channelId];
  if (!guide) {
    return nothing;
  }

  return html`
    <div class="channel-guide">
      <div class="channel-guide__title">${t("guide.title")} ${channelId}</div>
      <ol class="channel-guide__steps">
        ${guide.steps.map((step) => html`<li class="channel-guide__step">${step.text}</li>`)}
      </ol>
      ${
        guide.example
          ? html`
              <div class="channel-guide__example">
                <div class="channel-guide__example-header">
                  <span class="channel-guide__example-label">Example config:</span>
                  <button
                    type="button"
                    class="channel-guide__copy-btn"
                    @click=${(e: Event) => {
                      copyToClipboard(guide.example!, e.currentTarget as HTMLButtonElement);
                    }}
                  >
                    Copy
                  </button>
                </div>
                <pre class="channel-guide__example-code">${guide.example}</pre>
              </div>
            `
          : nothing
      }
      ${
        guide.docsUrl
          ? html`
              <a
                class="channel-guide__docs-link"
                href=${guide.docsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                ${t("guide.full_docs")}
              </a>
            `
          : nothing
      }
    </div>
  `;
}

/**
 * Render quick-start form fields for a channel whose plugin is not loaded yet.
 * Returns `nothing` if no fields are defined (falls back to JSON editor).
 */
export function renderChannelQuickForm(params: {
  channelId: string;
  value: unknown;
  path: Array<string | number>;
  disabled: boolean;
  onPatch: (path: Array<string | number>, value: unknown) => void;
}): TemplateResult | typeof nothing {
  const fields = CHANNEL_FIELDS[params.channelId];
  if (!fields || fields.length === 0) {
    return nothing;
  }

  const obj =
    params.value && typeof params.value === "object" && !Array.isArray(params.value)
      ? (params.value as Record<string, unknown>)
      : {};

  const patchField = (key: string, val: unknown) => {
    const next = { ...obj, [key]: val };
    // Remove empty string values so they don't clutter the config
    for (const [k, v] of Object.entries(next)) {
      if (v === "" || v === undefined) {
        delete next[k];
      }
    }
    params.onPatch(params.path, next);
  };

  // Always show an enabled toggle at the top
  const isEnabled = obj.enabled !== false;

  return html`
    <div class="channel-quick-form">
      <label class="channel-quick-form__toggle channel-quick-form__enabled">
        <div class="channel-quick-form__toggle-info">
          <span class="channel-quick-form__label">${t("picker.enabled")}</span>
        </div>
        <div class="cfg-toggle">
          <input
            type="checkbox"
            .checked=${isEnabled}
            ?disabled=${params.disabled}
            @change=${(e: Event) => patchField("enabled", (e.target as HTMLInputElement).checked)}
          />
          <span class="cfg-toggle__track"></span>
        </div>
      </label>
      ${fields.map((field) => {
        const currentValue = obj[field.key];

        if (field.type === "toggle") {
          const checked = currentValue === true;
          return html`
            <label class="channel-quick-form__toggle">
              <div class="channel-quick-form__toggle-info">
                <span class="channel-quick-form__label">${field.label}</span>
                ${field.help ? html`<span class="channel-quick-form__help">${field.help}</span>` : nothing}
              </div>
              <div class="cfg-toggle">
                <input
                  type="checkbox"
                  .checked=${checked}
                  ?disabled=${params.disabled}
                  @change=${(e: Event) => patchField(field.key, (e.target as HTMLInputElement).checked)}
                />
                <span class="cfg-toggle__track"></span>
              </div>
            </label>
          `;
        }

        const inputType = field.sensitive
          ? "password"
          : field.type === "number"
            ? "number"
            : "text";
        const displayValue = currentValue != null ? String(currentValue) : "";

        return html`
          <div class="channel-quick-form__field">
            <label class="channel-quick-form__label">${field.label}</label>
            ${field.help ? html`<div class="channel-quick-form__help">${field.help}</div>` : nothing}
            <input
              type=${inputType}
              class="cfg-input"
              placeholder=${field.placeholder ?? ""}
              .value=${displayValue}
              ?disabled=${params.disabled}
              @input=${(e: Event) => {
                const raw = (e.target as HTMLInputElement).value;
                if (field.type === "number") {
                  const num = Number(raw);
                  patchField(field.key, Number.isNaN(num) ? raw : num);
                } else {
                  patchField(field.key, raw);
                }
              }}
              @change=${(e: Event) => {
                const raw = (e.target as HTMLInputElement).value.trim();
                if (field.type === "number") {
                  const num = Number(raw);
                  patchField(field.key, raw === "" ? undefined : Number.isNaN(num) ? raw : num);
                } else {
                  patchField(field.key, raw);
                }
              }}
            />
          </div>
        `;
      })}
    </div>
  `;
}
